import React, {useId, useState} from 'react';
import {Box, Divider, IconButton, ListItemIcon, ListItemText, ListSubheader, Menu, MenuItem, Tooltip, useMediaQuery, useTheme} from '@mui/material';
import {MoreVert, Refresh, ViewColumnOutlined} from '@mui/icons-material';
import {alpha} from '@mui/material/styles';
import {getSettingsColors, settingsInputStyles} from '../settings/SettingsDesign';
import AirbnbDateRangePicker from './AirbnbDateRangePicker';

export const reportingToolbarStyles = {
    flexShrink: 0, minWidth: 0, px: {xs: 1.5, sm: 2}, py: 1.25,
    bgcolor: theme => getSettingsColors(theme).card, color: 'text.primary',
    '& .MuiButton-root': {minHeight: 36, borderRadius: '8px', textTransform: 'none', boxShadow: 'none', fontSize: 13},
    '& .MuiTypography-root': {whiteSpace: 'normal'}
};
export const reportingInputStyles = {
    ...settingsInputStyles, minWidth: 0,
    '& .MuiOutlinedInput-root': {...settingsInputStyles['& .MuiOutlinedInput-root'], minHeight: 38, borderRadius: '8px'},
    '& .MuiOutlinedInput-input': {...settingsInputStyles['& .MuiOutlinedInput-input'], fontSize: 13},
    '& .MuiOutlinedInput-notchedOutline': {border: 0},
    '& .MuiAutocomplete-inputRoot': {pr: '62px !important'},
    '& .MuiChip-root': {height: 24, fontSize: 12, borderRadius: '6px'}
};
export const reportingFilterInputStyles = {
    ...reportingInputStyles,
    '& .MuiOutlinedInput-root': {
        ...reportingInputStyles['& .MuiOutlinedInput-root'],
        bgcolor: theme => theme.palette.mode === 'dark' ? getSettingsColors(theme).inset : getSettingsColors(theme).input,
        '& .MuiOutlinedInput-notchedOutline': {
            border: 0
        }
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {border: 0},
    '& .MuiOutlinedInput-root.MuiAutocomplete-inputRoot': {
        bgcolor: theme => theme.palette.mode === 'dark' ? getSettingsColors(theme).inset : theme.palette.background.input
    }
};
export const reportingIconStyles = {
    width: 36, height: 36, borderRadius: '8px', color: 'text.secondary',
    bgcolor: theme => getSettingsColors(theme).inset,
    '&.Mui-disabled': {bgcolor: theme => getSettingsColors(theme).inset, color: 'text.disabled'},
    '&:hover': {bgcolor: theme => alpha(theme.palette.primary.main, 0.1), color: 'primary.main'},
    '& .MuiSvgIcon-root': {fontSize: 20}
};
export const reportingMenuPaperStyles = {
    bgcolor: theme => getSettingsColors(theme).card, color: 'text.primary', backgroundImage: 'none',
    borderRadius: '12px', border: '1px solid', borderColor: 'divider', boxShadow: 'none',
    width: 270, maxWidth: 'calc(100vw - 16px)',
    '& .MuiMenuItem-root': {minHeight: 38, fontSize: 13, mx: 0.5, borderRadius: '7px', whiteSpace: 'normal'},
    '& .MuiListItemText-primary': {fontSize: 13, whiteSpace: 'normal'},
    '& .MuiListItemIcon-root': {minWidth: 32, color: 'text.secondary'},
    '& .MuiSvgIcon-root': {fontSize: 19}
};
export const reportingGridStyles = {height: 'auto', flex: '1 1 0', minHeight: 0, minWidth: 0, position: 'relative'};

export function ReportActionMenu({actions, selectedCount = 0, label = 'Report actions'}) {
    const [anchor, setAnchor] = useState(null);
    const id = useId();
    return <>
        <Tooltip title={label}><IconButton aria-label={label} aria-haspopup="menu" aria-expanded={Boolean(anchor)}
            aria-controls={anchor ? id : undefined} onClick={event => setAnchor(event.currentTarget)} sx={reportingIconStyles}><MoreVert/></IconButton></Tooltip>
        <Menu id={id} open={Boolean(anchor)} anchorEl={anchor} onClose={() => setAnchor(null)}
            anchorOrigin={{vertical: 'bottom', horizontal: 'right'}} transformOrigin={{vertical: 'top', horizontal: 'right'}}
            slotProps={{paper: {sx: reportingMenuPaperStyles}, list: {'aria-label': label, dense: true}}}>
            <ListSubheader disableSticky sx={{px: 2, py: 1, fontSize: 12, lineHeight: 1.5, bgcolor: 'transparent', color: 'text.secondary'}}>
                {selectedCount ? `${selectedCount} selected` : 'Select rows to enable row actions'}
            </ListSubheader>
            {actions.filter(Boolean).flatMap((action, index, list) => [
                index > 0 && action.group !== list[index - 1].group ? <Divider key={`${action.label}-divider`} sx={{my: 0.5}}/> : null,
                <MenuItem key={action.label} disabled={action.disabled} onClick={() => {setAnchor(null); action.onClick();}}
                    sx={action.danger ? {color: 'error.main', '& .MuiListItemIcon-root': {color: 'error.main'}} : undefined}>
                    <ListItemIcon>{action.icon}</ListItemIcon><ListItemText primary={action.label}/>
                </MenuItem>
            ])}
        </Menu>
    </>;
}

export function ReportingDateControls({from, to, dateLabel, onChangeDate, onRefresh, onColumns, children, iconOnly = false, showDayNavigationOnMobile = false}) {
    const theme = useTheme();
    const mobile = useMediaQuery(theme.breakpoints.down('sm'));
    return <Box className="report-date-controls" sx={{display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, maxWidth: '100%', width: mobile && !iconOnly ? '100%' : undefined}}>
        <AirbnbDateRangePicker from={from} to={to} dateLabel={dateLabel} onChangeDate={onChangeDate} compact
            hideDayNavigation={mobile && !showDayNavigationOnMobile} iconOnly={iconOnly}/>
        {!mobile && onColumns && <Tooltip title="Column settings"><IconButton aria-label="column-settings" onClick={onColumns} sx={reportingIconStyles}><ViewColumnOutlined/></IconButton></Tooltip>}
        {children}
        <Tooltip title="Refresh report"><IconButton aria-label="refresh" onClick={onRefresh} sx={reportingIconStyles}><Refresh/></IconButton></Tooltip>
    </Box>;
}
